import { useCallback } from "react";
import { streamProcessor } from "../services";
import { create } from "mutative";
import {
  type Message,
  isAssistantTextMessage,
  isAssistantActionRequestMessage,
} from "../component/types";
import type { ToolCall } from "../component/types/toolCall";
import type { AIChatState } from "ee/reducers/uiReducers/aiChatReducer";
import * as MessageMapper from "../widget/mappers/messageMapper";
import * as MessageCitationMapper from "../widget/mappers/messageCitationMapper";
import type {
  ThreadCitationsContentData,
  ThreadErrorData,
  ThreadMessageData,
  ThreadMessageDeltaData,
  ThreadResponseCompletedData,
  ThreadRunCreatedData,
  ThreadSuggestionsContentData,
  ThreadToolCallRequestData,
} from "../types/messages";
import type { PromptSuggestion } from "../component/types/promptSuggestion";

interface UseStreamProcessingProps {
  updateState: (updates: Partial<AIChatState>) => void;
  appendMessage: (message: Message) => void;
  getErrorAssistantMessage: (message: string) => Message;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  logError: (message: string) => void;
  stopOperation: () => void;
}

export function useStreamProcessing({
  appendMessage,
  getErrorAssistantMessage,
  logError,
  setMessages,
  stopOperation,
  updateState,
}: UseStreamProcessingProps) {
  const updateFromStream = useCallback(
    async (response: Response, signal: AbortSignal) => {
      await streamProcessor.processStream(response, signal, {
        "thread.run.created": (data: unknown) => {
          const typedData = data as ThreadRunCreatedData;

          updateState({ threadId: typedData.threadId, runId: typedData.runId });
        },
        "thread.message.created": (data: unknown) => {
          const typedData = data as ThreadMessageData;

          updateState({ isWaitingForResponse: false });

          appendMessage(
            MessageMapper.fromDto({
              ...typedData,
              id: typedData.messageId,
              role: "assistant",
              type: "text",
              message: [
                {
                  text: {
                    value: "",
                  },
                  type: "text",
                },
              ],
            }),
          );
        },
        "thread.run.tool_call_request": (data: unknown) => {
          const typedData = data as ThreadToolCallRequestData;

          updateState({ runId: typedData.runId });

          const toolCalls: ToolCall[] = typedData.actions.map((toolCall) => ({
            id: toolCall.toolCallId,
            entity: {
              id: toolCall.function.entityId,
              type: toolCall.function.entityType,
              name: toolCall.function.name,
              previousMessageId: typedData.prevMessageId,
              iconLocation: toolCall.function.iconLocation,
              arguments: JSON.stringify(
                JSON.parse(toolCall.function.arguments),
              ),
            },
            output: toolCall.function.output,
            executionStatus: toolCall.isApprovalRequired
              ? "pendingApproval"
              : "pending",
            isApprovalRequired: toolCall.isApprovalRequired,
            outputSubmissionStatus: "pending",
          }));

          setMessages((prevMessages) =>
            create(prevMessages, (draft) => {
              // Find an existing action_request message with matching prevMessageId
              const existingIndex = draft.findIndex(
                (msg) =>
                  msg.type === "action_request" &&
                  msg.prevMessageId === typedData.prevMessageId,
              );

              if (existingIndex !== -1) {
                // Append new tool calls to the existing message
                const existingMessage = draft[existingIndex];

                if (Array.isArray(existingMessage.content)) {
                  existingMessage.content = [
                    ...existingMessage.content,
                    ...toolCalls,
                  ];
                }

                if (isAssistantActionRequestMessage(existingMessage)) {
                  existingMessage.runId = typedData.runId;
                  existingMessage.threadId = typedData.threadId;
                  existingMessage.responseId = typedData.responseId;
                }

                return;
              }

              // Otherwise, create a new message
              draft.push({
                id: typedData.runId,
                role: "assistant",
                type: "action_request",
                content: toolCalls,
                runId: typedData.runId,
                prevMessageId: typedData.prevMessageId,
                prevResponseId: typedData.prevResponseId,
                responseId: typedData.responseId,
                threadId: typedData.threadId,
                createdAt: Date.now(),
              });
            }),
          );
        },
        "thread.message.delta": (data: unknown) => {
          const typedData = data as ThreadMessageDeltaData;

          setMessages((prevMessages) =>
            create(prevMessages, (draft) => {
              const targetMessageIndex = draft.findIndex(
                (message) => message.id === typedData.messageId,
              );

              if (targetMessageIndex === -1) return;

              const message = draft[targetMessageIndex];

              message.content =
                message.content + typedData.delta.content[0].text.value;
            }),
          );
        },
        "thread.response.completed": (data: unknown) => {
          const typedData = data as ThreadResponseCompletedData;

          if (typedData.aMessageId) {
            setMessages((prevMessages) =>
              create(prevMessages, (draft) => {
                const targetMessageIndex = draft.findIndex(
                  (message) => message.id === typedData.messageId,
                );

                if (targetMessageIndex === -1) return;

                const message = draft[targetMessageIndex];

                message.id = typedData.aMessageId;
              }),
            );
          }
        },
        "citations.content": (data: unknown) => {
          const typedData = data as ThreadCitationsContentData;

          setMessages((prevMessages) =>
            create(prevMessages, (draft) => {
              const targetMessageIndex = draft.findIndex(
                (message) => message.id === typedData.aMessageId,
              );

              if (targetMessageIndex === -1) return;

              const message = draft[targetMessageIndex];

              if (!isAssistantTextMessage(message)) return;

              const citations = typedData.citations
                .map(MessageCitationMapper.fromDto)
                .filter((citation) => citation !== null);

              message.citations = citations;
            }),
          );
        },
        "suggestions.content": (data: unknown) => {
          const typedData = data as ThreadSuggestionsContentData;

          const suggestions: PromptSuggestion[] = typedData.suggestions.map(
            (suggestion: { title: string; prompt: string }) => {
              return {
                label: suggestion.title,
                value: suggestion.prompt,
              };
            },
          );

          // Attach the suggestions to the last message
          setMessages((prevMessages) =>
            create(prevMessages, (draft) => {
              const lastMessage = draft[draft.length - 1];

              // If the last message is not an Assistant text message,
              // we ignore the suggestions
              if (!isAssistantTextMessage(lastMessage)) return;

              lastMessage.promptSuggestions = suggestions;
            }),
          );
        },
        error: (data: unknown) => {
          let displayErrorMessage = "Unknown error";
          let loggingErrorMessage = "Unknown error";

          if (typeof data === "object" && data !== null && "message" in data) {
            const typedData = data as ThreadErrorData;

            displayErrorMessage = typedData.title ?? typedData.message;
            loggingErrorMessage = typedData.message;
          }

          stopOperation();
          logError(loggingErrorMessage);
          appendMessage(getErrorAssistantMessage(displayErrorMessage));
        },
      });
    },
    [
      updateState,
      appendMessage,
      setMessages,
      stopOperation,
      logError,
      getErrorAssistantMessage,
    ],
  );

  return {
    updateFromStream,
  };
}
